import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import './AdminScreen.css';

const AdminScreen = ({ user }) => {
  const { t } = useTranslation();
  const [maps, setMaps] = useState([]);
  const [selectedMap, setSelectedMap] = useState(null);
  const [points, setPoints] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [selectedPoint, setSelectedPoint] = useState({ x: 0, y: 0 });
  const [formData, setFormData] = useState({
    description: '',
    nivel: 5,
    categories: []
  });
  const [image, setImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const mapRef = useRef(null);

  // Fetch maps on component mount
  useEffect(() => {
    fetchMaps();
    fetchCategories();
  }, []);

  // Fetch points when a map is selected
  useEffect(() => {
    if (selectedMap) {
      fetchPoints(selectedMap.id);
    }
  }, [selectedMap]);

  // Fetch maps from the API
  const fetchMaps = async () => {
    setLoading(true);
    try {
      const response = await fetch('/api/admin/maps', {
        credentials: 'include'
      });
      if (!response.ok) {
        throw new Error('Failed to fetch maps');
      }
      const data = await response.json();
      setMaps(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Fetch points for a specific map
  const fetchPoints = async (mapId) => {
    setLoading(true);
    try {
      const response = await fetch(`/api/admin/maps/${mapId}/points`, {
        credentials: 'include'
      });
      if (!response.ok) {
        throw new Error('Failed to fetch points');
      }
      const data = await response.json();
      setPoints(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Fetch categories from the API
  const fetchCategories = async () => {
    setLoading(true);
    try {
      const response = await fetch('/api/admin/categories', {
        credentials: 'include'
      });
      if (!response.ok) {
        throw new Error('Failed to fetch categories');
      }
      const data = await response.json();
      setCategories(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Handle map selection
  const handleMapSelect = (map) => {
    setSelectedMap(map);
    setSelectedPoint({ x: 0, y: 0 });
    setFormData({
      description: '',
      nivel: 5,
      categories: []
    });
    setImage(null);
    setImagePreview(null);
    setError(null);
    setSuccess(null);
  };

  // Handle map click to select a point
  const handleMapClick = (e) => {
    if (!mapRef.current || !selectedMap) return;

    const rect = mapRef.current.getBoundingClientRect();
    const x = Math.round(e.clientX - rect.left);
    const y = Math.round(e.clientY - rect.top);

    setSelectedPoint({ x, y });
  };

  // Handle form input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
  };

  // Handle category selection
  const handleCategoryChange = (e) => {
    const categoryId = parseInt(e.target.value);
    const isChecked = e.target.checked;

    setFormData(prevData => {
      if (isChecked) {
        return {
          ...prevData,
          categories: [...prevData.categories, categoryId]
        };
      } else {
        return {
          ...prevData,
          categories: prevData.categories.filter(id => id !== categoryId)
        };
      }
    });
  };

  // Handle image selection
  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onloadend = () => {
      setImage(file);
      setImagePreview(reader.result);
    };
    reader.readAsDataURL(file);
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setSuccess(null);

    try {
      // First, create the point
      const pointResponse = await fetch('/api/admin/points', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        credentials: 'include',
        body: JSON.stringify({
          mapId: selectedMap.id,
          x: selectedPoint.x,
          y: selectedPoint.y,
          description: formData.description,
          nivel: parseInt(formData.nivel),
          categories: formData.categories
        })
      });

      if (!pointResponse.ok) {
        throw new Error('Failed to create point');
      }

      const pointData = await pointResponse.json();

      // If an image was selected, upload it
      if (image && imagePreview) {
        console.log('Uploading image for point:', pointData.id);

        const imageResponse = await fetch(`/api/admin/points/${pointData.id}/image`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          credentials: 'include',
          body: JSON.stringify({
            image: imagePreview,
            mapId: selectedMap.id
          })
        });

        if (!imageResponse.ok) {
          const errorData = await imageResponse.json();
          console.error('Image upload error:', errorData);
          throw new Error(`Failed to upload image: ${errorData.error || 'Unknown error'}`);
        }
      }

      // Reset form and show success message
      setFormData({
        description: '',
        nivel: 5,
        categories: []
      });
      setImage(null);
      setImagePreview(null);
      setSuccess('Point created successfully');

      // Refresh points list
      fetchPoints(selectedMap.id);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // If user is not authenticated, show login message
  if (!user) {
    return (
      <div className="admin-screen">
        <div className="container">
          <h1>{t('admin.title', 'Admin Panel')}</h1>
          <p>{t('admin.loginRequired', 'You must be logged in to access the admin panel')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-screen">
      <div className="container" style={{ backgroundColor: 'transparent' }}>
        <h1>{t('admin.title', 'Admin Panel')}</h1>

        {error && <div className="alert alert-danger">{error}</div>}
        {success && <div className="alert alert-success">{success}</div>}

        <div className="admin-content">
          <div className="admin-map-selection">
            <h2>{t('admin.selectMap', 'Select Map')}</h2>
            <div className="map-list">
              {maps.map(map => (
                <div 
                  key={map.id} 
                  className={`map-item ${selectedMap && selectedMap.id === map.id ? 'selected' : ''}`}
                  onClick={() => handleMapSelect(map)}
                  style={{
                    backgroundImage: `url(/images/maps/${map.id}.png)`,
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                    backgroundRepeat: 'no-repeat',
                    color: 'white',
                    textShadow: '1px 1px 2px black',
                    height: '80px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                >
                  {map.name}
                </div>
              ))}
            </div>
          </div>

          {selectedMap && (
            <div className="map-editor">
              <h2>{t('admin.mapEditor', 'Map Editor')}: {selectedMap.name}</h2>

              <div className="map-container">
                <div 
                  className="map-image" 
                  ref={mapRef}
                  onClick={handleMapClick}
                  style={{
                    width: selectedMap.width,
                    height: selectedMap.height,
                    backgroundImage: `url(/images/maps/${selectedMap.id}.png)`,
                    backgroundSize: 'contain',
                    backgroundRepeat: 'no-repeat',
                    backgroundPosition: 'center',
                    position: 'relative'
                  }}
                >
                  {/* Display existing points */}
                  {points.map(point => (
                    <div 
                      key={point.id}
                      className="point-marker existing"
                      style={{
                        left: point.x,
                        top: point.y
                      }}
                      title={point.description}
                    ></div>
                  ))}

                  {/* Display selected point */}
                  <div 
                    className="point-marker selected"
                    style={{
                      left: selectedPoint.x,
                      top: selectedPoint.y
                    }}
                  ></div>
                </div>
              </div>

              <div className="point-form">
                <h3>{t('admin.addPoint', 'Add New Point')}</h3>
                <p>
                  {t('admin.selectedCoordinates', 'Selected coordinates')}: X: {selectedPoint.x}, Y: {selectedPoint.y}
                </p>

                <form onSubmit={handleSubmit}>
                  <div className="form-group">
                    <label htmlFor="description">{t('admin.description', 'Description')}:</label>
                    <textarea
                      id="description"
                      name="description"
                      value={formData.description}
                      onChange={handleInputChange}
                      required
                    ></textarea>
                  </div>

                  <div className="form-group">
                    <label htmlFor="nivel">{t('admin.level', 'Difficulty Level')} (1-9):</label>
                    <input
                      type="number"
                      id="nivel"
                      name="nivel"
                      min="1"
                      max="9"
                      value={formData.nivel}
                      onChange={handleInputChange}
                      required
                    />
                  </div>

                  <div className="form-group">
                    <label>{t('admin.categories', 'Categories')}:</label>
                    <div className="categories-list">
                      {categories.map(category => (
                        <div key={category.id} className="category-item">
                          <input
                            type="checkbox"
                            id={`category-${category.id}`}
                            value={category.id}
                            checked={formData.categories.includes(category.id)}
                            onChange={handleCategoryChange}
                          />
                          <label htmlFor={`category-${category.id}`}>
                            {category.name} ({category.category})
                          </label>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="form-group">
                    <label htmlFor="image">{t('admin.image', 'Challenge Image')}:</label>
                    <input
                      type="file"
                      id="image"
                      accept="image/*"
                      onChange={handleImageChange}
                    />

                    {imagePreview && (
                      <div className="image-preview">
                        <img src={imagePreview} alt="Preview" />
                      </div>
                    )}
                  </div>

                  <button 
                    type="submit" 
                    className="btn btn-primary"
                    disabled={loading}
                  >
                    {loading ? t('admin.saving', 'Saving...') : t('admin.savePoint', 'Save Point')}
                  </button>
                </form>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminScreen;
